import React, { useEffect, useState } from "react";
import {
  Modal,
  Button,
  Form,
  Input,
  DatePicker,
  Row,
  Col,
  message,
  Select,
  TimePicker,
  Typography,
} from "antd";
import { useDispatch } from "react-redux";
import { CreateWorkshop, UpdateWorkshop } from "../redux/actions/WorkshopAction";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;
const dateFormat = "YYYY-MM-DD";
dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

const AddWorkshop = (props) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [visible, setVisible] = useState(false);
  const [loader, setLoader] = useState(false);

  const defaultWorkshopDate = props?.value?.workshopDate
    ? dayjs(props?.value?.workshopDate)
    : "";

  const defaultStartTime = props?.value?.startTime
    ? dayjs.utc(`${props?.value?.workshopDate.split('T')[0]}T${props?.value?.startTime}`).tz("Asia/Kolkata")
    : "";

  const defaultEndTime = props?.value?.endTime
    ? dayjs.utc(`${props?.value?.workshopDate.split('T')[0]}T${props?.value?.endTime}`).tz("Asia/Kolkata")
    : "";

  const handleCancel = () => {
    setVisible(false);
    form.resetFields();
  };

  const handleOk = () => {
    form.validateFields().then((values) => {
      if (props.value) {
        updateWorkshop(values);
      } else {
        addWorkshop(values);
      }
    }).catch((errorInfo) => {
      console.log('Validation failed:', errorInfo);
    });
  };

  const addWorkshop = (values) => {
    setLoader(true);
    
    // Combine the selected date with the start time
    const selectedDate = values.workshopDate.format("YYYY-MM-DD");
    const startTimeFormatted = values.startTime.tz("Asia/Kolkata").format("HH:mm:ss");
    const workshopDateWithStartTime = `${selectedDate}T${startTimeFormatted}`;
    
    const workshopData = {
      title: values.title,
      type: values.type,
      description: values.description || "",
      bannerImageUrl: values.bannerImageUrl || "",
      meetingUrl: values.meetingUrl || "",
      workshopDate: workshopDateWithStartTime,
      startTime: values.startTime.tz("Asia/Kolkata").format("HH:mm"),
      endTime: values.endTime.tz("Asia/Kolkata").format("HH:mm"),
      workshopDuration: values.workshopDuration,
    };

    const onSuccess = (res) => {
      setLoader(false);
      setVisible(false);
      form.resetFields();
      message.success("Workshop created successfully!", 8);
      if (props.getWorkshops) {
        // For new workshops, reset to page 1
        props.getWorkshops(1, true);
      }
    };

    const onError = (msg) => {
      setLoader(false);
      message.error(msg, 8);
    };

    dispatch(CreateWorkshop(workshopData, onSuccess, onError));
  };

  const updateWorkshop = (values) => {
    setLoader(true);
    
    // Combine the selected date with the start time
    const selectedDate = values.workshopDate.format("YYYY-MM-DD");
    const startTimeFormatted = values.startTime.tz("Asia/Kolkata").format("HH:mm:ss");
    const workshopDateWithStartTime = `${selectedDate}T${startTimeFormatted}`;
    
    const workshopData = {
      workshopId: props.value._id,
      title: values.title,
      type: values.type,
      description: values.description || "",
      bannerImageUrl: values.bannerImageUrl || "",
      meetingUrl: values.meetingUrl || "",
      workshopDate: workshopDateWithStartTime,
      startTime: values.startTime.tz("Asia/Kolkata").format("HH:mm"),
      endTime: values.endTime.tz("Asia/Kolkata").format("HH:mm"),
      workshopDuration: values.workshopDuration,
    };

    const onSuccess = (res) => {
      setLoader(false);
      setVisible(false);
      form.resetFields();
      message.success("Workshop updated successfully!", 8);
      if (props.getWorkshops) {
        // Refresh the current page instead of resetting to page 1
        props.getWorkshops(props.currentPage || 1, false);
      }
    };

    const onError = (msg) => {
      setLoader(false);
      message.error(msg, 8);
    };

    dispatch(UpdateWorkshop(workshopData, onSuccess, onError));
  };

  useEffect(() => {
    if (visible && props.value) {
      // Extract only the original title (before the first "|")
      const originalTitle = props.value.title ? props.value.title.split("|")[0].trim() : "";
      
      form.setFieldsValue({
        title: originalTitle,
        type: props.value.type,
        description: props.value.description || "",
        bannerImageUrl: props.value.bannerImageUrl || "",
        meetingUrl: props.value.meetingUrl || "",
        startTime: defaultStartTime,
        endTime: defaultEndTime,
        workshopDuration: props.value.workshopDuration,
      });
    }
  }, [visible, props.value, form]);

  return (
    <>
      <Button
        type={props.value ? "link" : "primary"}
        onClick={() => setVisible(true)}
        style={props.value ? { padding: 0, height: "auto" } : {}}
      >
        {props.value ? "Edit" : props.name || "Add Workshop"}
      </Button>

      <Modal
        title={
          <Title level={4}>
            {props.value ? "Edit Workshop" : "Add New Workshop"}
          </Title>
        }
        open={visible}
        onCancel={handleCancel}
        footer={[
          <Button key="cancel" onClick={handleCancel}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={loader}
            onClick={handleOk}
          >
            {props.value ? "Update" : "Create"}
          </Button>,
        ]}
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            type: "AI tools",
            workshopDuration: "30min",
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="title"
                label="Workshop Title"
                rules={[
                  { required: true, message: "Please enter workshop title!" },
                ]}
              >
                <Select placeholder="Select workshop title">
                  <Option value="English Demo">English Demo</Option>
                  <Option value="AI Tools">AI Tools</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="type"
                label="Workshop Type"
                rules={[
                  { required: true, message: "Please select workshop type!" },
                ]}
              >
                <Select placeholder="Select workshop type">
                  <Option value="AI tools">AI tools</Option>
                  <Option value="English Demo">English Demo</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="description"
            label="Description"
          >
            <TextArea
              rows={4}
              placeholder="Enter workshop description"
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="bannerImageUrl"
                label="Banner Image URL"
                rules={[
                  { type: "url", message: "Please enter a valid URL!" },
                ]}
              >
                <Input placeholder="https://example.com/images/banner.jpg" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="meetingUrl"
                label="Meeting URL"
                rules={[
                  { type: "url", message: "Please enter a valid URL!" },
                  { required: true, message: "Please add a valid meeting link!" },
                ]}
              >
                <Input placeholder="https://zoom.us/j/1234567890" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="workshopDate"
                label="Workshop Date"
                rules={[
                  { required: true, message: "Please select workshop date!" },
                ]}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  format={dateFormat}
                  placeholder={props.value?.workshopDate ? `Previous: ${props.value.workshopDate.split('T')[0]}` : "Select date"}
                  onClick={() => {
                    if (props.value) {
                      form.setFieldsValue({ workshopDate: "" });
                    }
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="startTime"
                label="Start Time"
                rules={[
                  { required: true, message: "Please select start time!" },
                ]}
              >
                <TimePicker
                  style={{ width: "100%" }}
                  format="HH:mm"
                  placeholder="Select start time"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="endTime"
                label="End Time"
                rules={[
                  { required: true, message: "Please select end time!" },
                ]}
              >
                <TimePicker
                  style={{ width: "100%" }}
                  format="HH:mm"
                  placeholder="Select end time"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="workshopDuration"
            label="Workshop Duration"
            rules={[
              { required: true, message: "Please enter workshop duration!" },
            ]}
          >
            <Select placeholder="Select duration">
              <Option value="15min">15 minutes</Option>
              <Option value="30min">30 minutes</Option>
              <Option value="45min">45 minutes</Option>
              <Option value="60min">1 hour</Option>
              <Option value="90min">1.5 hours</Option>
              <Option value="120min">2 hours</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default AddWorkshop; 